import React, { useContext, useEffect, useState } from "react";
import Gallery from "../../../components/Gallery/Gallery";
import AttachmentApi from "../../../api/services/AttachmentApi";
import AttachmentFileHandler from "../../../helper/AttachmentFileHandler";
import FileReference from "../../../components/Gallery/model/FileReference.model";
import LoadedFile from "../../../components/Gallery/model/LoadedFile.model";
import Attachment from "../../../components/Gallery/model/Attachment.model";
import GalleryContext from "../../../contexts/GalleryContext";
import useEnv from "../../../hooks/env";
import TestRequestApi from "../../../api/services/TestRequestApi";
import ProductAttachment from "../../../api/models/product/ProductAttachment";
import ProductApi from "../../../api/services/ProductApi";
import { AttachmentType } from "../../../api/models/AttachmentType";
import replaceSpecialChars from "../../../functions/replaceSpecialChars.function";

type AttachmentResponse = ProductRaw.Schemas.AttachmentResponse;

export interface CompetitorProductGalleryProps {
  companyName: string | undefined;
  editable: boolean;
  productId: number;
  productName: string;
  productAttachments: AttachmentResponse[];
  onChange?: (value: ProductAttachment[]) => void;
}

const CompetitorProductGallery: React.FC<CompetitorProductGalleryProps> = (props) => {
  const { companyName, editable, productAttachments, onChange, productId, productName } = props;
  const { flags } = useContext(GalleryContext);
  const { api } = useEnv();
  const attachmentApi = new AttachmentApi(api);
  const productApi = new ProductApi(api);
  const testRequestApi = new TestRequestApi(api);

  const mapAttachmentResponse = (res: AttachmentResponse): Attachment => ({
    uuid: res.attachmentReference,
    editable: "READ_WRITE",
    flag: flags?.find((f) => f.type === res.type),
  });
  const mapProductAttachment = (attachment: Attachment): ProductAttachment => ({
    ...attachment,
    type: attachment.flag?.type,
  });
  const namingStrategy = {
    nameDetailDialog(file: LoadedFile) {
      return {
        header: `GALLERY ${productName}`,
        subheader: file.name,
      };
    },
  };

  const attachmentFileHandler = new AttachmentFileHandler(
    attachmentApi,
    {
      remove: (idToBeDeleted) => {
        return productApi
          .removeAttachmentCompetitorProduct(productId, idToBeDeleted[0])
          .then((res) => res.map(mapAttachmentResponse))
          .then((attachments) => ({ attachments, idToBeDeleted }));
      },
      append: (ids, flag) =>
        testRequestApi
          .getClearanceToken(ids[0])
          .then(({ data: token }) =>
            productApi
              .addAttachmentCompetitorProduct(productId, (flag?.type as AttachmentType) || "OTHER", token)
              .then((res) => res.map(mapAttachmentResponse)),
          ),
      zipFileName(files: FileReference[]): string {
        if (files.length === 1) {
          const file = files[0];
          if (file.status === "LOADED") {
            return (file as LoadedFile).name;
          }
          return file.id;
        }
        return `LEAP-${replaceSpecialChars(companyName || "", "_")}-${replaceSpecialChars(productName, "_")}.zip`;
      },
    },
    () => productApi.fetchClearanceTokenCompetitorProduct(productId).then(({ data }) => data),
  );

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  useEffect(() => {
    setAttachments(productAttachments.map(mapAttachmentResponse));
  }, [productAttachments]);

  return (
    <Gallery
      disabled={!editable}
      hideIfEmpty
      galleryId={`CompetitorProduct${productId}`}
      fileHandler={attachmentFileHandler}
      namingStrategy={namingStrategy}
      attachments={attachments}
      onChange={(values) => {
        onChange?.(values.map(mapProductAttachment));
      }}
    />
  );
};

export default CompetitorProductGallery;
